import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', amber:'#F59E0B' };

function extractId(raw) {
  const m = (raw || '').match(/verify-tutor[\/\\]([A-Z0-9-]+)/i);
  return m ? m[1] : raw.trim().toUpperCase();
}

export default function AdminCheckin() {
  const [searchParams] = useSearchParams();
  const [input,  setInput]   = useState('');
  const [result, setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  // PIN management
  const [currentPin, setCurrentPin]   = useState('');
  const [newPin, setNewPin]           = useState('');
  const [pinSaving, setPinSaving]     = useState(false);
  const [pinMsg, setPinMsg]           = useState('');
  const [showPinSection, setShowPinSection] = useState(false);

  const inputRef = useRef(null);
  const didAutoScan = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    // Load current PIN
    api.get('/api/admin/checkin-pin')
      .then(r => setCurrentPin(r.data.pin || ''))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam && !didAutoScan.current) {
      didAutoScan.current = true;
      handleScan(idParam);
    }
  }, [searchParams]); // eslint-disable-line

  const handleScan = async (raw) => {
    const idNumber = extractId(raw);
    if (!idNumber) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post('/api/admin/tutor-checkin', { tutor_id_number: idNumber });
      const data = r.data;
      setResult(data);
      setHistory(h => [{ ...data, ts: new Date(), idNumber }, ...h.slice(0, 19)]);
      const name = data.tutor_name || data.name;
      if (data.action === 'checkin') toast.success(`${name} checked in`);
      else toast.success(`${name} checked out`);
    } catch (e) {
      const msg = e.response?.data?.error || 'ID not found';
      setResult({ error: true, message: msg });
      toast.error(msg);
    }
    setLoading(false);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const savePin = async () => {
    if (!newPin || !/^\d{4,8}$/.test(newPin)) {
      setPinMsg('PIN must be 4–8 digits'); return;
    }
    setPinSaving(true); setPinMsg('');
    try {
      await api.put('/api/admin/checkin-pin', { pin: newPin });
      setCurrentPin(newPin);
      setNewPin('');
      setPinMsg('✅ PIN updated successfully');
      toast.success('Check-in PIN updated');
    } catch (e) {
      setPinMsg(e.response?.data?.error || 'Failed to save');
    }
    setPinSaving(false);
  };

  const typeIcon = (type) => type === 'tutor' ? '🎓' : '🎒';

  return (
    <Layout role="admin">
      <div style={{ maxWidth:580, margin:'0 auto', padding:'8px 0' }}>

        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:C.navy }}>Check-In / Check-Out</h2>
          <p style={{ margin:0, fontSize:14, color:C.grey }}>Works for tutors (TUT-…) and students (TAL-…). First scan = in, second = out.</p>
        </div>

        {/* Scanner input */}
        <div style={{ background:'#fff', borderRadius:14, padding:20, border:'1.5px solid #E5E7EB', marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.navy, marginBottom:10 }}>🔢 Enter or Scan ID</div>
          <div style={{ display:'flex', gap:10 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleScan(input)}
              placeholder="e.g. TUT-2026-0001 or TAL-2026-0001"
              style={{ flex:1, padding:'12px 14px', border:'2px solid #E5E7EB', borderRadius:10, fontSize:14, fontFamily:'monospace', outline:'none' }}
              autoComplete="off"
            />
            <button onClick={() => handleScan(input)} disabled={loading || !input.trim()}
              style={{ padding:'12px 20px', background: loading ? '#E5E7EB' : C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? '…' : 'Log'}
            </button>
          </div>
          <div style={{ fontSize:12, color:C.grey, marginTop:8 }}>
            💡 USB QR scanner auto-submits. On mobile, scan QR → enter PIN on verify page.
          </div>
        </div>

        {/* Result */}
        {result && (
          <div style={{
            borderRadius:14, padding:20, marginBottom:16,
            background: result.error ? '#FEF2F2' : result.action === 'checkin' ? '#F0FDF4' : '#EFF6FF',
            border: `1.5px solid ${result.error ? '#FECACA' : result.action === 'checkin' ? '#BBF7D0' : '#BFDBFE'}`,
          }}>
            <div style={{ fontSize:24, marginBottom:6 }}>
              {result.error ? '❌' : result.action === 'checkin' ? '✅' : '👋'}
            </div>
            <div style={{ fontWeight:700, fontSize:16, color:C.navy, marginBottom:3 }}>
              {result.error ? 'Not Found' : (result.tutor_name || result.name)}
              {!result.error && result.type && (
                <span style={{ marginLeft:8, fontSize:13, fontWeight:500, color:C.grey }}>
                  {result.type === 'tutor' ? '🎓 Tutor' : '🎒 Student'}
                </span>
              )}
            </div>
            <div style={{ fontSize:14, color:C.grey }}>{result.message}</div>
            {result.action === 'checkout' && result.duration_min != null && (
              <div style={{ marginTop:8, fontSize:14, fontWeight:700, color:'#1D4ED8' }}>
                ⏱ Time: {Math.floor(result.duration_min/60)}h {result.duration_min%60}m
              </div>
            )}
          </div>
        )}

        {/* Today's log */}
        {history.length > 0 && (
          <div style={{ background:'#fff', borderRadius:14, padding:20, border:'1px solid #E5E7EB', marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.navy, marginBottom:12 }}>Today's Log</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {history.map((h, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #F1F5F9' }}>
                  <span style={{ fontSize:16 }}>{h.action === 'checkin' ? '🟢' : '🔵'}</span>
                  <span style={{ fontSize:14 }}>{typeIcon(h.type)}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:C.navy, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {h.tutor_name || h.name}
                    </div>
                    <div style={{ fontSize:11, color:C.grey, fontFamily:'monospace' }}>{h.idNumber}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:12, fontWeight:700, color: h.action === 'checkin' ? C.success : '#1D4ED8' }}>
                      {h.action === 'checkin' ? 'In' : 'Out'}
                    </div>
                    <div style={{ fontSize:11, color:C.grey }}>
                      {h.ts.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PIN Management */}
        <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', overflow:'hidden' }}>
          <button
            onClick={() => setShowPinSection(s => !s)}
            style={{ width:'100%', padding:'16px 20px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:18 }}>🔑</span>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>Check-In PIN</div>
                <div style={{ fontSize:12, color:C.grey }}>
                  {currentPin ? `Current PIN: ${'●'.repeat(currentPin.length)}` : 'No PIN set'}
                </div>
              </div>
            </div>
            <span style={{ color:C.grey, fontSize:16 }}>{showPinSection ? '▲' : '▼'}</span>
          </button>

          {showPinSection && (
            <div style={{ padding:'0 20px 20px', borderTop:'1px solid #F1F5F9' }}>
              <p style={{ fontSize:13, color:C.grey, margin:'12px 0' }}>
                Staff enter this PIN on the tutor/student verify page to log attendance without logging in.
                Share only with receptionists and teachers.
              </p>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={newPin}
                  onChange={e => { setNewPin(e.target.value.replace(/\D/g,'')); setPinMsg(''); }}
                  placeholder="New PIN (4–8 digits)"
                  maxLength={8}
                  style={{ flex:1, padding:'10px 14px', border:'2px solid #E5E7EB', borderRadius:10, fontSize:16, fontFamily:'monospace', outline:'none', letterSpacing:4 }}
                />
                <button
                  onClick={savePin}
                  disabled={pinSaving || !newPin}
                  style={{ padding:'10px 20px', background: pinSaving || !newPin ? '#E5E7EB' : C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor: pinSaving || !newPin ? 'not-allowed' : 'pointer' }}>
                  {pinSaving ? '…' : 'Save'}
                </button>
              </div>
              {pinMsg && (
                <div style={{ marginTop:8, fontSize:13, color: pinMsg.startsWith('✅') ? C.success : C.error, fontWeight:600 }}>
                  {pinMsg}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}
